import { PcbNodeTypes, getSubTreeUids, pcbLayoutRootNodeId, pcbLayoutRootNodeParentId, } from "@buildwithflux/models";
import { guid, Records } from "@buildwithflux/shared";
import remove from "lodash/remove";
// eslint-disable-next-line boundaries/element-types
import { pcbRuleImportantString } from "../pcb/layout_engine/constants";
/**
 * Remove the passed in `childUids` from a parentNode
 *
 * @param pcbNodes - The nodes map we are updating childUids
 * @param parentUid - Uid of parent node we are removing childUids from
 * @param childUidsToRemove - Array of child uids to remove
 * @param parentNodeNotFoundStrategy - Whether or not to log an error if parent node is not found. By default this is always "log"
 */
export const removeChildUidsFromNode = (pcbNodes, parentUid, childUidsToRemove, parentNodeNotFoundStrategy = "log") => {
    // Skip updating parent node if it is a `ROOT` node
    if (parentUid === pcbLayoutRootNodeParentId)
        return;
    const parentNode = pcbNodes[parentUid];
    if (!parentNode) {
        if (parentNodeNotFoundStrategy === "log") {
            /* eslint-disable no-console */
            console.error("Trying to remove childUids from a non-existed node", parentUid);
        }
        return;
    }
    const childUids = parentNode.childUids;
    // NOTE: Here `childUids` might not exist when baking footprint node -> remove virtual nodes
    // from origin
    if (childUids) {
        for (const childUid of childUidsToRemove) {
            remove(childUids, (uid) => uid === childUid);
        }
    }
};
/**
 * Remove the passed in nodes from map
 *
 * @param pcbNodes - The nodes map we are removing nodes from
 * @param nodeUids - Array of uids of nodes we are removing
 */
export const removePcbNodesFromMap = (pcbNodes, nodeUids) => {
    for (const nodeUid of nodeUids) {
        delete pcbNodes[nodeUid];
    }
};
/**
 * Remove descendants of all passed in nodes from the map
 *
 * @param pcbNodes - The nodes map we are removing nodes from
 * @param nodeUids - Array of uids of nodes, we will find and remove descendants of each node
 * @param parentNodeNotFoundStrategy - Whether or not to log an error if parent node is not found. By default this is always "log"
 *
 * @returns uids of all descendants being removed
 */
const removeDescendantNodesFromMap = (pcbNodes, nodeUids) => {
    const nodesToRemove = [];
    for (const nodeUid of nodeUids) {
        nodesToRemove.push(...getSubTreeUids({
            pcbNodes,
            nodeUid,
        }));
    }
    removePcbNodesFromMap(pcbNodes, nodesToRemove);
    return nodesToRemove;
};
/**
 * Remove nodes from existing collection - could be a map or other data strucutre, can use
 * function overload to achieve that
 *
 * This function will remove both the passed in nodes and their descendants
 *
 * @param pcbNodes - The nodes map we are removing nodes from
 * @param nodeUids - Array of uids of nodes we are removing
 * @param parentNodeNotFoundStrategy - Whether or not to log an error if parent node is not found. By default this is always "log"
 * @param nodeNotFoundStrategy - Whether or not to log an error if node to remove is not found. By default this is always "log"
 *
 * @returns array of uids we removed
 */
export const removeNodesAndDescendants = (pcbNodes, nodeUids, parentNodeNotFoundStrategy = "log", nodeNotFoundStrategy = "log") => {
    const allNodesToRemove = [...nodeUids];
    // Remove childUids from parent first
    let errorSeen = false;
    for (const nodeUid of nodeUids) {
        const nodeToRemove = pcbNodes[nodeUid];
        if (!nodeToRemove) {
            if (errorSeen === false && nodeNotFoundStrategy === "log") {
                /* eslint-disable no-console */
                console.warn("Trying to remove a non-existent node", nodeUid);
                errorSeen = true;
            }
            continue;
        }
        removeChildUidsFromNode(pcbNodes, nodeToRemove.parentUid, [nodeToRemove.uid], parentNodeNotFoundStrategy);
    }
    // Remove descendant nodes
    allNodesToRemove.push(...removeDescendantNodesFromMap(pcbNodes, nodeUids));
    /// Remove the node
    removePcbNodesFromMap(pcbNodes, nodeUids);
    return allNodesToRemove;
};
export const removeOrphanedSegments = (pcbNodes) => {
    for (const nodeId of Object.keys(pcbNodes)) {
        const node = pcbNodes[nodeId];
        // NOTE: node could be undefined here its parent is orphaned and removed from previous iteration.
        // By that time the entire sub-tree is removed.
        if (!node || node.type !== PcbNodeTypes.routeSegment || node.parentUid === pcbLayoutRootNodeId) {
            continue;
        }
        const parentNode = pcbNodes[node.parentUid];
        // Orphaned segment case1: parent node is not undefined, then call these internal functions
        // to remove the sub-tree WITHOUT trying to update parent node's childUids
        if (!parentNode) {
            removeDescendantNodesFromMap(pcbNodes, [node.uid]);
            removePcbNodesFromMap(pcbNodes, [node.uid]);
            continue;
        }
        // Orphaned segment case2: parent node is defined but its childUids does not includ this node.
        // This case we can safely call removeNodesAndDescendants
        const parentNodeChildUids = parentNode.childUids;
        if (!parentNodeChildUids || !parentNodeChildUids.includes(nodeId)) {
            removeNodesAndDescendants(pcbNodes, [nodeId]);
        }
    }
};
export const toggleNodesWithImportant = (draftState, toggleBakedNodes) => {
    const someActive = toggleBakedNodes.some((node) => node.bakedRules.active);
    const destState = !someActive;
    for (const node of toggleBakedNodes) {
        const selector = `[uid=${node.uid}]`;
        const destRuleset = Records.findValue(draftState.pcbLayoutRuleSets, (rs) => rs.selector === selector) ?? {
            name: `Override rule for ${node.name}`,
            uid: guid(),
            selector,
            rules: {},
            referenceOfDocumentUid: draftState.uid,
        };
        const destRule = Records.findValue(destRuleset.rules, (r) => r.key === "active") ?? {
            uid: "active",
            key: "active",
            disabled: false,
            value: "",
        };
        destRule.value = `${destState} ${pcbRuleImportantString}`;
        destRuleset.rules[destRule.key] = destRule;
        const canRemoveRule = destState && Records.count(destRuleset.rules) === 1;
        if (canRemoveRule) {
            delete draftState.pcbLayoutRuleSets[destRuleset.uid];
        }
        else {
            draftState.pcbLayoutRuleSets[destRuleset.uid] = destRuleset;
        }
    }
};
//# sourceMappingURL=removeNodes.js.map